import { useCallback, useEffect, useRef, useState } from "react";
import type { AgentCursor } from "@/stores/canvas-store";

/** Question confirmations may linger; ordinary progress follows its live owner. */
export function useCompactPillState(
  current: AgentCursor | null,
  hovered: boolean,
) {
  const [snapshot, setSnapshot] = useState<AgentCursor | null>(current);
  const [visible, setVisible] = useState(!!current);
  const dismissing = useRef(false);
  const nextRef = useRef(current);
  nextRef.current = current;
  const ordinaryHandoff = !dismissing.current && !!snapshot && !snapshot.question && (
    current?.agentId !== snapshot.agentId ||
    current?.executionId !== snapshot.executionId ||
    current?.sessionId !== snapshot.sessionId
  );

  useEffect(() => {
    if (ordinaryHandoff) {
      setSnapshot(current);
      setVisible(!!current);
      return;
    }
    if (dismissing.current) return;
    if (current?.agentId === snapshot?.agentId) {
      if (current && current !== snapshot) setSnapshot(current);
      if (current && !visible) setVisible(true);
      return;
    }
    if (snapshot) {
      if (!current && hovered) return;
      setVisible(false);
      return;
    }
    if (current) {
      setSnapshot(current);
      setVisible(true);
    }
  }, [current, hovered, ordinaryHandoff, snapshot, visible]);

  const dismiss = useCallback(() => {
    dismissing.current = true;
    setVisible(false);
  }, []);

  const onExited = useCallback(() => {
    dismissing.current = false;
    const successor = nextRef.current;
    setSnapshot(successor);
    setVisible(!!successor);
  }, []);

  const [hiddenQuestionIds, setHiddenQuestionIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const hideQuestion = useCallback((id: string) => {
    setHiddenQuestionIds((old) => new Set(old).add(id));
  }, []);

  return {
    snapshot: ordinaryHandoff ? current : snapshot,
    visible: ordinaryHandoff ? !!current : visible,
    dismiss,
    onExited,
    hiddenQuestionIds,
    hideQuestion,
  };
}
